/* eslint-disable react-hooks/exhaustive-deps */
import { has } from '@/commonFunctions/utils'
import { ButtonPrimary } from '@/components/button'
import { Close, InValidUser } from '@/components/icons'
import { BRACKET_MODIFY, LIMIT, OFFSET } from '@/constants/Common'
import { useDebounce } from '@/hooks/useDebounce'
import { useAppSelector } from '@/hooks/useRedux'
import { clubSelector } from '@/pages/club/selectors'
import { userSelector } from '@/pages/user/selectors'
import { useGetPlayRole, useSearchPlayer } from '@/services'
import { Bracket, Player } from '@/types'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from '../../../components/search'
import { PlayerItemLoading } from '../../../components/skeleton'
import { BracketPlayerItem } from './BracketPlayerItem'
import { ClubMembershipModal } from './ClubMembershipModal'

type Props = {
  open: boolean
  onClose: () => void
  onSubmit: ({ playerId, isClubMember }: { playerId?: number; isClubMember?: boolean }) => any
  bracket: Bracket
}

export const AddParticipantModal = ({ open, onClose, onSubmit, bracket }: Props) => {
  const { t } = useTranslation()

  const { user } = useAppSelector(userSelector)
  const { permissions } = useAppSelector(clubSelector)

  const [searchText, setSearchText] = useState('')
  const [players, setPlayers] = useState<Player[]>([])
  const [offset, setOffset] = useState(OFFSET)
  const [checked, setChecked] = useState<Player | null>(null)
  const [isConfirm, setIsConfirm] = useState(false)

  const { mutate: searchPlayer, data: resSearchPlayer, isLoading } = useSearchPlayer()
  const { mutate: getPlayRole } = useGetPlayRole()

  const debouncedSearchText = useDebounce<string>(searchText || '*', 200)

  const openConfirmModal = () => setIsConfirm(true)

  const closeConfirmModal = () => setIsConfirm(false)

  const handleClubMembership = () => {
    if (bracket && checked)
      getPlayRole(
        { bracketId: bracket.bracketId.toString(), userId: checked?.id },
        {
          onSuccess: () => {
            openConfirmModal()
          },
        }
      )
  }

  const onChangeMemberSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
    setPlayers([])
    setOffset(OFFSET)
  }

  const handleScroll = (event: any) => {
    const { target } = event
    const hasMore = resSearchPlayer?.result.hasMore

    if (target.scrollHeight - target.scrollTop === target.clientHeight && hasMore) {
      setOffset((pre) => pre + LIMIT)
    }
  }

  const onCloseModal = () => {
    onClose()
    setPlayers([])
    setSearchText('')
    setChecked(null)
    setOffset(OFFSET)
  }

  useEffect(() => {
    if (open && bracket)
      searchPlayer({
        clubId: bracket.clubId.toString(),
        limit: LIMIT,
        offset: offset,
        query: debouncedSearchText ? debouncedSearchText : '*',
        filter: {
          lat: user?.addresses?.[0]?.latitude || null,
          lng: user?.addresses?.[0]?.longitude || null,
          gender: bracket.playerGroup === 'MEN' ? 'MALE' : bracket.playerGroup === 'WOMEN' ? 'FEMALE' : (null as any),
          rating: {
            maxRating: null,
            minRating: null,
            type: bracket.format,
          },
          ageRange: {
            maxAge: bracket.ageBracket[1],
            minAge: bracket.ageBracket[0],
          },
        },
        bracketId: bracket.bracketId.toString(),
        ...(has(permissions, BRACKET_MODIFY) && {
          pageSource: 'LD_ADD_PARTICIPANT',
        }),
        includeUnclaimedPlayers: true,
      })
  }, [open, offset, debouncedSearchText, bracket])

  useEffect(() => {
    if (resSearchPlayer) setPlayers([...players, ...resSearchPlayer.result.hits])
  }, [resSearchPlayer])

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onCloseModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col gap-y-4 max-w-5xl transform rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={onCloseModal}
                >
                  <Close className="text-navy-900" />
                </button>

                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('event.addABracketParticipant')}
                </Dialog.Title>

                <div className="flex">
                  <p className="text-sm text-navy-900 text-opacity-70">{t('event.selectAPlayer')}</p>
                </div>

                <Search
                  searchText={searchText}
                  onChangeInput={onChangeMemberSearch}
                  className="py-3 px-4 w-full border gap-x-2"
                />

                <div className="flex items-center gap-x-2 rounded px-4 py-[10px] border-primary border-[2px]">
                  <InValidUser className="w-7 h-7" />
                  <p className="text-sm text-navy-900 text-opacity-70">{t('event.addABracketParticipantDesc')}</p>
                </div>

                <div className="flex flex-col gap-y-2 h-96 overflow-y-auto" onScroll={(e: any) => handleScroll(e)}>
                  {players?.map((item) => (
                    <BracketPlayerItem
                      key={item.id}
                      item={item}
                      className="border !rounded-lg"
                      showCheckBox
                      checked={checked}
                      setChecked={setChecked}
                    />
                  ))}
                  {isLoading && (
                    <>
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                      <PlayerItemLoading className="!max-w-[964px]" />
                    </>
                  )}
                </div>
                <div className="flex justify-center gap-x-6">
                  <button
                    onClick={onCloseModal}
                    className="bg-navy-900 hover:bg-opacity-20 bg-opacity-10 !py-[6px] !px-4 !rounded text-navy-900 font-semibold"
                  >
                    {t('common.cancel')}
                  </button>
                  <ButtonPrimary
                    title={t('event.addParticipant')}
                    className="!py-[6px] !px-4 !rounded"
                    disabled={!checked}
                    onClick={handleClubMembership}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>

        <ClubMembershipModal
          open={isConfirm}
          onClose={closeConfirmModal}
          onSubmit={(value) => {
            onSubmit({ playerId: checked?.id, isClubMember: value })
            closeConfirmModal()
            onCloseModal()
          }}
        />
      </Dialog>
    </Transition>
  )
}
